import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Languages } from "lucide-react";

const RELATIONS = ["Mother", "Father", "Grandparent", "Uncle", "Aunt", "Other"];

// LAMA text fields we auto-translate to Hindi. On translate, the Hindi text is
// appended below the English text so the printed PDF shows both.
const TRANSLATABLE = [
  ["lama_reason", "Reason (as stated by guardian)"],
  ["condition_at_discharge", "Condition at time of leaving"],
  ["treatment_given", "Treatment given during stay"],
];

export default function LamaForm({ f, updateField }) {
  const [translating, setTranslating] = useState(false);
  const translateToHindi = async () => {
    const src = {};
    TRANSLATABLE.forEach(([k]) => {
      const v = (f[k] || "").trim();
      // Strip prior Hindi appendage so re-translating doesn't compound.
      const base = v.split(/\n\n— हिन्दी —\n/)[0];
      if (base) src[k] = base;
    });
    if (!Object.keys(src).length) return toast.info("Fill the English fields first");
    setTranslating(true);
    try {
      const { data } = await api.post("/ai/translate-hi", { fields: src });
      const tr = data.translated || {};
      Object.entries(tr).forEach(([k, hi]) => {
        const base = (f[k] || "").split(/\n\n— हिन्दी —\n/)[0].trim();
        updateField(k, `${base}\n\n— हिन्दी —\n${hi}`);
      });
      toast.success("Hindi translation added — preview / print to see bilingual output");
    } catch (e) { toast.error(e?.response?.data?.detail || "Translation failed"); }
    finally { setTranslating(false); }
  };
  return (
    <div className="card-clinical p-5 border-l-4 border-l-rose-500" data-testid="lama-form">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-rose-500/15 text-rose-600 dark:text-rose-300">
            <AlertTriangle size={18} />
          </div>
          <div>
            <div className="font-medium">Leave Against Medical Advice</div>
            <div className="text-xs text-muted-foreground">All fields feed the LAMA declaration form for printing.</div>
          </div>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={translateToHindi}
                disabled={translating} data-testid="lama-translate">
          {translating ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Languages size={12} className="mr-1" />}
          {translating ? "Translating…" : "Add Hindi translation"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Reason (as stated by guardian)" full>
          <Textarea rows={3} value={f.lama_reason || ""} onChange={(e) => updateField("lama_reason", e.target.value)} data-testid="lama-reason"
                    placeholder="e.g., financial constraints, wants transfer, preference for home care…" />
        </Field>

        <Field label="Guardian name">
          <Input value={f.lama_guardian_name || ""} onChange={(e) => updateField("lama_guardian_name", e.target.value)} data-testid="lama-guardian" />
        </Field>

        <Field label="Relation to patient">
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={f.lama_relation || ""} onChange={(e) => updateField("lama_relation", e.target.value)}
            data-testid="lama-relation"
          >
            <option value="">Select…</option>
            {RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>

        <Field label="Witness name (optional)">
          <Input value={f.lama_witness_name || ""} onChange={(e) => updateField("lama_witness_name", e.target.value)} />
        </Field>

        <Field label="Signature name (guardian, as they will sign)">
          <Input value={f.lama_signature_name || ""} onChange={(e) => updateField("lama_signature_name", e.target.value)} />
        </Field>

        <div className="md:col-span-2 flex items-center gap-3 rounded-lg border border-border p-3 bg-muted/30">
          <input
            id="lama-risks"
            type="checkbox"
            checked={!!f.lama_risks_explained}
            onChange={(e) => updateField("lama_risks_explained", e.target.checked)}
            className="h-4 w-4 accent-rose-500"
            data-testid="lama-risks"
          />
          <label htmlFor="lama-risks" className="text-sm">
            I have explained the medical condition of the child, the risks of leaving against advice
            (including deterioration and mortality), and the guardian understood and still wishes to leave.
          </label>
        </div>

        <Field label="Condition at time of leaving" full>
          <Textarea rows={3} value={f.condition_at_discharge || ""} onChange={(e) => updateField("condition_at_discharge", e.target.value)} data-testid="lama-condition" />
        </Field>
        <Field label="Treatment given during stay (short)" full>
          <Textarea rows={3} value={f.treatment_given || ""} onChange={(e) => updateField("treatment_given", e.target.value)} data-testid="lama-treatment" />
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
